// RPTEXPORT1 (docs/build-briefs/RPTEXPORT1-pdf-and-google-sheets-export.md): the pure half of the Export PDF and Export to
// Google Sheets buttons. No DOM, so a plain node:test file can import it.
//
// RPTEXPORT1-F2: Google Sheets needs only the PUBLIC OAuth client id, which the page learns from the same session-scoped
// export-options route the older Export menu uses. There is no connection to check: the person signs in to Google in their own
// browser when they press the button.

/** Where the export options of a Reports host live: the tenant or franchisor root of its prefix. */
export function exportOptionsPath(prefix: string): string {
  return `${prefix.replace(/\/reports(\/.*)?$/, "")}/export-options`;
}

/** The export path of a report with the format named, replacing a format already on it. */
export function withFormat(path: string, format: "csv" | "pdf" | "sheets"): string {
  const [base = "", query = ""] = path.split("?");
  const params = new URLSearchParams(query);
  params.set("format", format);
  return `${base}?${params.toString()}`;
}

/** loading: the options are not answered yet. not_set_up: no client id is configured. ready: the person may press it.
 * error: the options could not be read; a press tries again. */
export type SheetsState = "loading" | "not_set_up" | "ready" | "error";

/** The public client id from an export-options reply, or null when none is configured. Undefined: not a readable reply. */
export function clientIdFromOptions(status: number, body: unknown): string | null | undefined {
  if (status !== 200 || typeof body !== "object" || body === null) return undefined;
  const value = (body as Record<string, unknown>)["googleSheetsClientId"];
  if (value === null) return null;
  return typeof value === "string" && value !== "" ? value : value === "" ? null : undefined;
}

/** What the Google Sheets button says and whether it can be pressed. */
export function sheetsButtonFace(state: SheetsState): {
  label: string;
  disabled: boolean;
  title: string;
} {
  if (state === "not_set_up") {
    return {
      label: "Google Sheets is not set up",
      disabled: true,
      title: "Ask a Snip & Sip team member to finish the Google Sheets setup.",
    };
  }
  return {
    label: "Export to Google Sheets",
    disabled: state === "loading",
    title: "Signs you in to Google and creates a new sheet in your own Google Drive.",
  };
}

/** A spreadsheet address the screen may open: only Google's own sheets host over https. */
export function safeSheetUrl(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" &&
      url.hostname === "docs.google.com" &&
      url.pathname.startsWith("/spreadsheets/")
      ? url.toString()
      : undefined;
  } catch {
    return undefined;
  }
}

/** The plain sentence for a refused or failed export. */
export function exportFailureMessage(status: number, body: unknown): string {
  // The API's error body is { error: <code>, message }.
  const code =
    typeof body === "object" && body !== null
      ? (body as Record<string, unknown>)["error"]
      : undefined;
  if (code === "google_sheets_not_configured") return "Google Sheets is not set up.";
  const message =
    typeof body === "object" && body !== null
      ? (body as Record<string, unknown>)["message"]
      : undefined;
  if (typeof message === "string" && message !== "") return message;
  return status === 403
    ? "You do not have permission to export this report."
    : "Could not export this report.";
}
